import React, { Fragment, FC, memo } from 'react';
import { Opt } from 'ts-opt';
import { AnimalList } from 'api/gen/AnimalList';
import { AssociationType } from 'api/gen/AssociationType';
import { CriteriaDefinition } from 'api/gen/CriteriaDefinition';
import { CriteriaList } from 'api/gen/CriteriaList';
import { ModalListConfirmName, ModalListsOverview, ModalAddAnimals, ModalAddAnimalsFromDb } from 'app/modals/components';
import { useOurTranslation } from 'app/translations';
import { useAnimalModals } from '../../hooks/use-animal-modals';
import { ModalCriteriaListName } from '../modal-criteria-list-name';
import { ModalCriteriaListNameConfirm } from '../modal-criteria-list-name-confirm';
import { ModalGetCriteriaDetails } from '../modal-get-criteria-details';
import { ModalMultipleSaveList } from '../modal-multiple-save-list';
const MALE = 'MALE';
interface Props {
  /** Association type used by the tab workflow. */
  associationType: AssociationType;
  /** Whether the animals page is empty. */
  isAnimalsPageEmpty: boolean;
  /** Saved animal lists available to the user. */
  animalLists: Opt<Array<AnimalList>>;
  /** Saved criteria lists available to the user. */
  criteriaLists: Opt<Array<CriteriaList>>;
  /** Criteria definitions shown in the modal tree. */
  criteriaDefinitions: Opt<Array<CriteriaDefinition>>;
  /** Table-settings names used by the save-list modal. */
  tableSettingsNameList: Array<string>;
  /** Opens or closes the saved animal lists modal. */
  toggleAnimalListsVisibility(): void;
  /** Opens or closes the add-animal modal. */
  toggleAddAnimalsVisibility(): void;
  /** Opens or closes the add-from-DB modal. */
  toggleAddAnimalsFromDBVisibility(): void;
  /** Opens or closes the overview modal for saved lists. */
  toggleListsOverviewVisibility(): void;
  /** Opens or closes the saved criteria lists modal. */
  toggleCriteriaListsVisibility(): void;
  /** Opens or closes the criteria-list-details modal. */
  toggleCriteriaListDetailsVisibility(): void;
}
const MatingBullsModalsBase: FC<Props> = ({
  associationType,
  isAnimalsPageEmpty,
  animalLists,
  criteriaLists,
  tableSettingsNameList,
  criteriaDefinitions,
  toggleAnimalListsVisibility,
  toggleAddAnimalsVisibility,
  toggleAddAnimalsFromDBVisibility,
  toggleListsOverviewVisibility,
  toggleCriteriaListsVisibility,
  toggleCriteriaListDetailsVisibility
}) => {
  const {
    animalListId,
    availableAnimals,
    loading: hookLoading,
    visibility,
    actions
  } = useAnimalModals({
    associationType,
    criteriaDefinitions,
    sex: MALE
  });
  const {
    t,
    tCommon
  } = useOurTranslation('mating');
  return <Fragment>
            <ModalListConfirmName visible={visibility.animalListsConfirm} animalListId={animalListId} onSubmit={actions.confirmAnimalList} onCancel={actions.toggleOverwriteAnimalListVisibility} loading={hookLoading.confirmAnimalListName} listAlreadyExistsMessage={t('listAlreadyExists')} data-sentry-element="ModalListConfirmName" data-sentry-source-file="bulls-modals.tsx" />
            <ModalMultipleSaveList visible={visibility.animalLists} isAnimalsPageEmpty={isAnimalsPageEmpty} animalListEmptyMessage={t('atLeastOneAnimal')} onSubmitSaveToDb={actions.createAnimalList} onSubmitSaveToFile={actions.getAnimalsCsv} onCancel={toggleAnimalListsVisibility} animalLists={animalLists} title={t('saveBulls')} data-sentry-element="ModalMultipleSaveList" data-sentry-source-file="bulls-modals.tsx" />
            <ModalAddAnimals onCancel={toggleAddAnimalsVisibility} onSearch={actions.getAvailableAnimals} onSubmit={actions.addAnimals} visible={visibility.addAnimals} title={t('fillLineAndRegister')} fieldLabel={tCommon('linReg')} submitLabel={tCommon('add')} availableAnimals={availableAnimals} loadingAvailableAnimals={hookLoading.availableAnimals} setAvailableAnimalsLoading={actions.setAvailableAnimalsLoading} data-sentry-element="ModalAddAnimals" data-sentry-source-file="bulls-modals.tsx" />
            <ModalAddAnimalsFromDb visible={visibility.addAnimalsFromDb} animalLists={animalLists} title={t('chooseAnimalList')} submitLabel={tCommon('add')} selectLabel={t('animalList')} animalListsLoading={hookLoading.animalLists} onCancel={toggleAddAnimalsFromDBVisibility} onSubmit={actions.addAnimalsFromDb} data-sentry-element="ModalAddAnimalsFromDb" data-sentry-source-file="bulls-modals.tsx" />
            <ModalListsOverview visible={visibility.listsOverview} animalLists={animalLists} criteriaLists={criteriaLists} onCancel={toggleListsOverviewVisibility} renameAnimalList={actions.renameAnimalList} renameCriteriaList={actions.renameCriteriaList} deleteAnimalList={actions.deleteAnimalList} deleteCriteriaList={actions.deleteCriteriaList} noListsSaved={t('noSavedLists')} title={t('managementOfSavedLists')} areYouSureToDeleteMessage={t('areYouSure')} listAlreadyExistsMessage={t('listAlreadyExists')} data-sentry-element="ModalListsOverview" data-sentry-source-file="bulls-modals.tsx" />
            <ModalCriteriaListName criteriaLists={criteriaLists} tableSettingsNameList={tableSettingsNameList} visible={visibility.criteriaLists} onSubmit={actions.createCriteriaList} onCancel={toggleCriteriaListsVisibility} data-sentry-element="ModalCriteriaListName" data-sentry-source-file="bulls-modals.tsx" />
            <ModalCriteriaListNameConfirm visible={visibility.criteriaListsConfirm} onSubmit={actions.confirmCriteriaList} onCancel={actions.toggleOverwriteCriteriaListVisibility} loading={hookLoading.confirmCriteriaListName} data-sentry-element="ModalCriteriaListNameConfirm" data-sentry-source-file="bulls-modals.tsx" />
            <ModalGetCriteriaDetails visible={visibility.criteriaListDetails} criteriaLists={criteriaLists} onSubmit={actions.getCriteriaListDetails} onCancel={toggleCriteriaListDetailsVisibility} data-sentry-element="ModalGetCriteriaDetails" data-sentry-source-file="bulls-modals.tsx" />
        </Fragment>;
};

/**
 * Memoized modal container for the bull selection tab.
 *
 * @param props Modal state and handlers for the bull workflow.
 * @returns The bull modal stack.
 *
 * @example
 * <MatingBullsModals
 *   associationType="HOLSTEIN"
 *   isAnimalsPageEmpty={false}
 *   animalLists={none}
 *   criteriaLists={none}
 *   criteriaDefinitions={none}
 *   tableSettingsNameList={[]}
 *   toggleAnimalListsVisibility={noop}
 *   toggleAddAnimalsVisibility={noop}
 *   toggleAddAnimalsFromDBVisibility={noop}
 *   toggleListsOverviewVisibility={noop}
 *   toggleCriteriaListsVisibility={noop}
 *   toggleCriteriaListDetailsVisibility={noop}
 * />
 */
export const MatingBullsModals = memo(MatingBullsModalsBase);